import React, { useMemo } from 'react';
import type { WidgetSize } from '../../types/dashboard';
import { fmtMoney } from '../../utils/formatters';
import { getTradePnl, getTradeTimestampMs, normalizeTradeStatus } from '../../lib/trades/authoritativeTradeData';
import { useDisplayTimezone } from '../app/DisplayTimezoneProvider';

interface PerformanceByDayOfWeekWidgetProps {
  timeRange: 'week' | 'month' | 'year';
  size: WidgetSize;
  trades?: any[];
}

export const PerformanceByDayOfWeekWidget: React.FC<PerformanceByDayOfWeekWidgetProps> = ({ 
  timeRange, 
  size, 
  trades = [],
}) => {
  const displayTimeZone = useDisplayTimezone();
  const perfData = useMemo(() => {
    const daysOfWeek = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];

    const realizedTrades = trades.filter((trade) => normalizeTradeStatus(trade) === 'closed');
    if (!realizedTrades.length) return { items: [], maxPnl: 1 };

    const buckets = new Map(daysOfWeek.map((day) => [day, { name: day, pnl: 0 }]));
    realizedTrades.forEach((trade) => {
      const timestampMs = getTradeTimestampMs(trade);
      if (timestampMs == null) return;
      const day = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: displayTimeZone }).format(timestampMs);
      const bucket = buckets.get(day);
      if (!bucket) return;
      bucket.pnl += getTradePnl(trade);
    });

    const data = daysOfWeek.map(day => buckets.get(day) ?? { name: day, pnl: 0 });
    
    const totalPnlVolume = data.reduce((acc, d) => acc + Math.abs(d.pnl), 0);
    const maxPnl = Math.max(1, ...data.map(d => Math.abs(d.pnl)));

    return {
      items: data.map(d => ({
        ...d,
        pct: totalPnlVolume > 0 ? (Math.abs(d.pnl) / totalPnlVolume) * 100 : 0,
      })),
      maxPnl,
    };
  }, [timeRange, trades, displayTimeZone]);

  if (!perfData.items.length) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-zinc-200 text-sm text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
        Import Trades to see the widget
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-auto pr-2">
        <div className={`space-y-2.5 ${size === 'mini' ? 'space-y-1' : 'space-y-2.5'}`}>
          {perfData.items.map(item => (
            <div key={item.name} className="border-b border-zinc-200 dark:border-zinc-800 pb-2 last:border-b-0">
              <div className={`flex justify-between items-center ${size === 'mini' ? 'text-xs' : 'text-sm'}`}>
                <span className="font-semibold w-32">{item.name}</span>
                <div className="flex items-center gap-4">
                  <span className={`font-semibold w-28 text-right ${item.pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {fmtMoney(item.pnl)}
                  </span>
                  <span className="text-xs text-zinc-500 dark:text-zinc-400 w-12 text-right">
                    {item.pct.toFixed(item.pct < 1 ? 2 : 1)}%
                  </span>
                </div>
              </div>
              {item.pnl !== 0 && (
                <div className="w-full bg-zinc-200 dark:bg-zinc-700/50 rounded-full h-1.5 mt-1">
                  <div
                    className={`${item.pnl >= 0 ? 'bg-emerald-500' : 'bg-red-500'} h-1.5 rounded-full`}
                    style={{ width: `${(Math.abs(item.pnl) / perfData.maxPnl) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
